import { useState, useMemo } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "../../ui/Card";
import {
  Button,
  Container,
  Form,
  Row,
  Col,
  Table,
} from "react-bootstrap";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
  BarChart,
  Bar,
  Legend,
  CartesianGrid,
} from "recharts";

// ---------------- Monte Carlo Simulation ----------------

function runMonteCarlo({
  winRate,
  rr,
  trades,
  startingBalance,
  risk,
  maxDrawdownPct,
  profitTargetPct,
  avgTradeExpense
}) {
  //Convert win rate and drawdown to a decimal
  winRate = winRate / 100;
  const iterations = 500;
  const paths = [];
  let passCount = 0;

  for (let i = 0; i < iterations; i++) {
    let balance = startingBalance;
    let peak = startingBalance;
    let breached = false;
    let passed = false;
    let failTradeCount = 0;
    let passTradeCount = 0;
    let maxDrawdown = 0;
    let sumWins = 0;
    let sumLosses = 0;
    const path = [{ trade: 0, balance }];

    for (let t = 1; t <= trades; t++) {
      const win = Math.random() < winRate;
      const reward = win ? rr * risk : -1 * risk;
      balance += reward - avgTradeExpense;
      if (win) {
        sumWins = sumWins + rr * risk;
      } else {
        sumLosses = sumLosses + risk;
      }
      path.push({ trade: t, balance });
      if (balance > peak) peak = balance;
      const dd = 100 * (peak - balance) / peak;
      if (dd > maxDrawdown) {
        maxDrawdown = dd;
      }
      if (!breached && !passed) {
        if (maxDrawdownPct && dd > maxDrawdownPct) {
          breached = true;
          failTradeCount = t;
          //break;
        }
        const balancePct = 100 * (balance - startingBalance) / startingBalance;
        
        if (profitTargetPct && balancePct > profitTargetPct) {
          passed = true;
          passTradeCount = t;
        }
      }

    }
    //Need to include if it passes too
    //Include challenge fee
    //Include form that has how many challenges to purchase and expected ROI
    const profitFactor = sumWins / sumLosses;
    if (!breached && passed) passCount++
    paths.push({ path, balance, breached, passed, failTradeCount, passTradeCount, maxDrawdown, profitFactor });
  }

  return { paths, passProbability: passCount / iterations };
}

const calculateAverage = (arr) => {
  if (arr.length === 0) {
    return 0; // Handle empty array case to avoid division by zero
  }
  let sum = 0;
  for (let i = 0; i < arr.length; i++) {
    sum += arr[i];
  }
  return sum / arr.length;
}

// ---------------- Component ----------------

export default function ChallengeCalculator() {
  const [winRate, setWinRate] = useState(60);
  const [rr, setRR] = useState(1);
  const [trades, setTrades] = useState(100);
  const [startingBalance, setStartingBalance] = useState(50000);
  const [risk, setRisk] = useState(250);
  const [maxDrawdownPct, setMaxDrawdownPct] = useState(5);
  const [profitTargetPct, setProfitTargetPct] = useState(6);
  const [minBalance, setMinBalance] = useState(0);
  const [maxBalance, setMaxBalance] = useState(100000);
  const [finalBalance, setFinalBalance] = useState(0);
  const [percentGain, setPercentGain] = useState(0);
  const [avgTradeExpense, setAvgTradeExpense] = useState(8);
  const [avgPassTradeCount, setAvgPassTradeCount] = useState(0);
  const [avgFailTradeCount, setAvgFailTradeCount] = useState(0);
  const [avgProfitFactor, setAvgProfitFactor] = useState(0);
  const [avgDrawdown, setAvgDrawdown] = useState(0);

  const monteCarlo = useMemo(() => {
    return runMonteCarlo({ winRate, rr, trades, startingBalance, risk, maxDrawdownPct, profitTargetPct, avgTradeExpense });
  }, [winRate, rr, risk, trades, startingBalance, maxDrawdownPct, profitTargetPct, avgTradeExpense]);

  // Probability of finishing above starting balance
  const probabilityPass = useMemo(() => {
    return monteCarlo.passProbability * 100;
  }, [monteCarlo]);

  // Histogram of outcomes
  const histogram = useMemo(() => {
    const bins = {};
    monteCarlo.paths.forEach(({ path }) => {
      const final = Math.round(path[path.length - 1].balance / 10) * 10;
      bins[final] = (bins[final] || 0) + 1;
    });
    const values = Object.entries(bins).map(([k, v]) => ({
      value: Number(k),
      count: v,
    })).sort((a, b) => a.value - b.value);
    return values;
  }, [monteCarlo]);

  // Confidence bands
  const confidenceData = useMemo(() => {
    const steps = trades + 1;
    let bands = [];

    for (let t = 0; t < steps; t++) {

      const values = monteCarlo.paths.map(({ path }) =>
        path[t]?.balance
      ).filter((p) => p !== undefined).sort((a, b) => a - b);

      const low = values[Math.floor(values.length * 0.025)];
      const high = values[Math.floor(values.length * 0.975)];
      const median = values[Math.floor(values.length * 0.5)];

      bands.push({ trade: t, low, high, median });
    }
    bands = bands.filter((band) => band.low !== undefined && band.high !== undefined && band.median !== undefined)
    const finalBand = bands[bands.length - 1];
    setFinalBalance(finalBand.median);
    setPercentGain((100 * (finalBand.median - startingBalance) / startingBalance).toFixed(2));
    const avgPassCountArray = monteCarlo.paths.map((path) => {return path.passTradeCount}).filter((count) => count > 0);
    const avgFailCountArray = monteCarlo.paths.map((path) => {return path.failTradeCount}).filter((count) => count > 0);
    const avgProfitFactorArray = monteCarlo.paths.map((path) => {return path.profitFactor}).filter((count) => count > 0);
    const avgDrawdownArray = monteCarlo.paths.map((path) => {return path.maxDrawdown}).filter((count) => count > 0);
    setAvgPassTradeCount(calculateAverage(avgPassCountArray));
    setAvgFailTradeCount(calculateAverage(avgFailCountArray));
    setAvgProfitFactor(calculateAverage(avgProfitFactorArray));
    setAvgDrawdown(calculateAverage(avgDrawdownArray));
    setMinBalance(bands[0].low);
    setMaxBalance(bands[bands.length - 1].high);
    return bands;
  }, [monteCarlo, trades]);

  const formatCurrency = (value) => {
    return `$${value?.toFixed(2).toLocaleString()}`; // Adds dollar sign and formats with commas
  };

  const generateTicks = (min, max) => {
    let step = 1000;
    step = Math.round((max - min) / 10 / step) * step;
    if (step === 0) {
      step = Math.ceil((max - min) / 10 / 100) * 100;
    }

    const ticks = [];
    if (min > startingBalance) {
      min = startingBalance - step;
    }
    // Ensure the start value is a multiple of the step if necessary, or just use min
    for (let i = min; i <= max; i += step) {
      ticks.push(Math.round(i / step) * step);
    }
    return ticks;
  };

  return (
    <div className="p-6 space-y-6 max-w-5xl mx-auto">

      {/* Controls */}

      <Row>
        <Col></Col>
        <Col xs={12} md={6} className="bg-light mt-3 ml-3 mb-4 mr-3 pt-3 pb-3 justify-content-left border p-3">
          <h3 className="border-bottom mb-2 pb-2">
            Investment Settings
          </h3>
          <Row>
            <Col>
              <Form.Group className="mb-3" controlId="winRate">
                <Form.Label><h5>Win Rate (%)</h5></Form.Label>
                <Form.Control
                  value={winRate}
                  onChange={(e) => setWinRate(Number(e.target.value))}
                  type="number"
                  placeholder="Win Rate"
                  name="winRate"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="risk">
                <Form.Label><h5>Risk ($)</h5></Form.Label>
                <Form.Control
                  value={risk}
                  onChange={(e) => setRisk(Number(e.target.value))}
                  type="number"
                  placeholder="Risk"
                  name="risk"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="rr">
                <Form.Label><h5>Reward to Risk (RR)</h5></Form.Label>
                <Form.Control
                  value={rr}
                  onChange={(e) => setRR(Number(e.target.value))}
                  type="number"
                  placeholder="Reward to Risk"
                  name="rr"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="avgTradeExpense">
                <Form.Label><h5>Average Trade Expense ($)</h5></Form.Label>
                <Form.Control
                  value={avgTradeExpense}
                  onChange={(e) => setAvgTradeExpense(Number(e.target.value))}
                  type="number"
                  placeholder="Average Trade Expense"
                  name="avgTradeExpense"
                  required
                />
              </Form.Group>
            </Col>
            <Col>
              <Form.Group className="mb-3" controlId="trades">
                <Form.Label><h5>Number of Trades</h5></Form.Label>
                <Form.Control
                  value={trades}
                  onChange={(e) => setTrades(Number(e.target.value))}
                  type="number"
                  placeholder="Trades"
                  name="trades"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="startingBalance">
                <Form.Label><h5>Starting Balance ($)</h5></Form.Label>
                <Form.Control
                  value={startingBalance}
                  onChange={(e) => setStartingBalance(Number(e.target.value))}
                  type="number"
                  placeholder="Starting Balance"
                  name="startingBalance"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="maxDrawdownPct">
                <Form.Label><h5>Max Drawdown (%)</h5></Form.Label>
                <Form.Control
                  value={maxDrawdownPct}
                  onChange={(e) => setMaxDrawdownPct(Number(e.target.value))}
                  type="number"
                  placeholder="Max Drawdown Percentage"
                  name="maxDrawdownPct"
                  required
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="profitTargetPct">
                <Form.Label><h5>Profit Target (%)</h5></Form.Label>
                <Form.Control
                  value={profitTargetPct}
                  onChange={(e) => setProfitTargetPct(Number(e.target.value))}
                  type="number"
                  placeholder="Profit Target Percent"
                  name="profitTargetPct"
                  required
                />
              </Form.Group>
            </Col>
          </Row>
        </Col>
        <Col xs={12} md={4} className="mt-3 ml-3 mb-4 mr-3 pt-3 pb-3 justify-content-left border p-3">
          <h3 className="border-bottom mb-2 pb-2">
            Projected Outcome
          </h3>
          <Table striped bordered hover>
            <thead>
              <tr>
                <th>Metric</th>
                <th>Value</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  Probability of Passing Challenge
                </td>
                <td>
                  {probabilityPass.toFixed(1)}%
                </td>
              </tr>
              <tr>
                <td>
                  Average Trades to Pass Challenge
                </td>
                <td>
                  {avgPassTradeCount.toFixed(1)}
                </td>
              </tr>
              <tr>
                <td>
                  Average Trades to Fail Challenge
                </td>
                <td>
                  {avgFailTradeCount.toFixed(1)}
                </td>
              </tr>
              <tr>
                <td>
                  Profit Factor
                </td>
                <td>
                  {avgProfitFactor.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td>
                  Max Drawdown
                </td>
                <td>
                  {formatCurrency(avgDrawdown / 100 * startingBalance)}
                </td>
              </tr>
              <tr>
                <td>
                  Final Balance
                </td>
                <td className={finalBalance >= startingBalance ? "text-success" : "text-danger"}>
                  <strong>{formatCurrency(finalBalance)}</strong>
                </td>
              </tr>
              <tr>
                <td>
                  Proft/Loss
                </td>
                <td className={finalBalance >= startingBalance ? "text-success" : "text-danger"}>
                  <strong>{formatCurrency(finalBalance - startingBalance)}</strong>
                </td>
              </tr>
              <tr>
                <td>
                  Return On Investment (%)
                </td>
                <td className={finalBalance >= startingBalance ? "text-success" : "text-danger"}>
                  <strong>{percentGain}%</strong>
                </td>
              </tr>
            </tbody>
          </Table>
        </Col>
        <Col></Col>
      </Row>


      {/* <Container>
        <Row>
          <Col xs={12} md={4} className="mt-3 ml-3 mb-4 mr-3 pt-3 pb-3 justify-content-left border p-3">
            <h3 className="border-bottom mb-2 pb-2">
              Complete Results Comparison
            </h3>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Metric</th>
                  <th>Value</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    Probability of Passing Challenge
                  </td>
                  <td>
                    {probabilityPass.toFixed(1)}%
                  </td>
                </tr>
                <tr>
                  <td>
                    Final Balance
                  </td>
                  <td>
                    {formatCurrency(finalBalance)}
                  </td>
                </tr>
                <tr>
                  <td>
                    Proft/Loss
                  </td>
                  <td>
                    {formatCurrency(finalBalance - startingBalance)}
                  </td>
                </tr>
              </tbody>
            </Table>
          </Col>
        </Row>
      </Container> */}


      {/* Confidence Band Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Equity Curve</CardTitle>
          <div className={finalBalance >= startingBalance ? "text-success" : "text-danger"}>
            <h5>
              {finalBalance >= startingBalance ? <i className="bi bi-arrow-up-circle-fill"></i> : <i className="bi bi-arrow-down-circle-fill"></i>} {formatCurrency(finalBalance - startingBalance)} ({percentGain}%) over {trades} trades
            </h5>
          </div>
        </CardHeader>

        <CardContent>
          <ResponsiveContainer width="100%" height={600}>
            <LineChart
              data={confidenceData}
              margin={{ top: 0, right: 30, left: 50, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#ababab95" />
              <XAxis
                dataKey="trade"
                label={{
                  value: 'Number of Trades',
                  position: 'insideBottom',
                  offset: -10,
                  fill: '#333',
                }}
              />
              <YAxis
                type="number"
                ticks={generateTicks(minBalance, maxBalance)}
                interval={0}
                tickFormatter={formatCurrency}
                domain={[
                  minBalance - 100, maxBalance + 100,
                ]}
                label={{
                  value: 'Balance ($)',
                  angle: -90,
                  position: 'insideLeft',
                  offset: -30,
                  fill: '#333',
                }}
              />
              <Tooltip />
              <Legend
                verticalAlign="bottom"
                height={36}
                wrapperStyle={{ paddingTop: 30 }} // Pushes the legend down 30px from its default position
              />
              <Line dataKey="low" stroke="black" type="basis" strokeWidth={2} dot={false} name="Worst Outcome" />
              <Line dataKey="high" stroke="green" type="basis" strokeWidth={2} dot={false} name="Best Outcome" />
              <Line dataKey="median" stroke="blue" type="basis" strokeWidth={2} dot={false} name="Average Balance" />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Histogram */}
      <Card>
        <CardHeader>
          <CardTitle>Equity Distribution</CardTitle>
        </CardHeader>

        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={histogram}>
              <XAxis dataKey="value" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
}
